import { useState, useEffect } from "react";
import {
  MoreHorizontal,
  Shield,
  User,
  Users,
  Mail,
  Loader2,
  Trash2,
  Ban,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  adminApi,
  type UserResponse,
} from "@/api/services/admin";
import { InviteUserDialog } from "./InviteUserDialog";
import { useApiToken } from "@/components/providers/token-provider";
import { isAdminUser } from "@/lib/auth";

export function UsersTab() {
  const { user: currentUser } = useApiToken();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 10;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers(page, pageSize, search);
      setUsers(response.users);
      setTotal(response.total);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
        fetchUsers();
    }, 300); // Debounce search
    return () => clearTimeout(timer);
  }, [page, search]);

  const handleResendInvite = async (userId: string) => {
    try {
      await adminApi.resendInvite(userId);
      toast.success("Invitation resent successfully");
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const handleChangeRole = async (userId: string, role: "admin" | "member") => {
    try {
      await adminApi.changeUserRole(userId, role);
      toast.success(`Role updated to ${role}`);
      // Ideally we would update local state, but we don't display role yet.
    } catch (error) {
      toast.error("Failed to change role");
    }
  };

  const handleToggleStatus = async (userId: string, enabled: boolean) => {
    try {
      if (enabled) {
        await adminApi.disableUser(userId);
        toast.success("User disabled");
      } else {
        await adminApi.enableUser(userId);
        toast.success("User enabled");
      }
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${enabled ? "disable" : "enable"} user`);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this user? This action cannot be undone.")) return;
    try {
      await adminApi.removeUser(userId);
      toast.success("User removed");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };

  const isSelf = (userId: string) => currentUser?.keycloak_id === userId;
  const isAdmin = isAdminUser(currentUser);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </CardTitle>
            <CardDescription className="mt-2">
              Manage users and their permissions.
            </CardDescription>
          </div>
          {isAdmin && <InviteUserDialog onSuccess={fetchUsers} />}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center py-4">
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="max-w-sm"
          />
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {user.first_name} {user.last_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                        {user.enabled ? (
                          <Badge
                            variant="default"
                            className="bg-green-500 hover:bg-green-600"
                          >
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Disabled</Badge>
                        )}
                        {!user.email_verified && (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                            Invited
                          </Badge>
                        )}
                        {user.mfa_enabled ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            MFA
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                            No MFA
                          </span>
                        )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                                onClick={() => navigator.clipboard.writeText(user.email)}
                            >
                                Copy Email
                            </DropdownMenuItem>
                            
                            {isAdmin && (
                              <>
                                <DropdownMenuSeparator />
                                
                                {!user.email_verified && (
                                    <DropdownMenuItem onClick={() => handleResendInvite(user.id)}>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Resend Invite
                                    </DropdownMenuItem>
                                )}

                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                        <Shield className="mr-2 h-4 w-4" />
                                        Change Role
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => handleChangeRole(user.id, "admin")}>
                                            <div className="flex items-center w-full justify-between">
                                                <div className="flex items-center">
                                                    <Shield className="mr-2 h-4 w-4" />
                                                    Admin
                                                </div>
                                                {user.role === "admin" && <CheckCircle className="h-4 w-4 ml-2" />}
                                            </div>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleChangeRole(user.id, "member")}>
                                            <div className="flex items-center w-full justify-between">
                                                <div className="flex items-center">
                                                    <User className="mr-2 h-4 w-4" />
                                                    Member
                                                </div>
                                                {user.role === "member" && <CheckCircle className="h-4 w-4 ml-2" />}
                                            </div>
                                        </DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>

                                {!isSelf(user.id) && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleToggleStatus(user.id, user.enabled)}>
                                            {user.enabled ? (
                                                <>
                                                    <Ban className="mr-2 h-4 w-4" />
                                                    Disable Account
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle className="mr-2 h-4 w-4" />
                                                    Enable Account
                                                </>
                                            )}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleRemoveUser(user.id)} className="text-destructive focus:text-destructive">
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Remove User
                                        </DropdownMenuItem>
                                    </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={users.length < pageSize || loading} // Simple check, ideally check total
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
